import React, { FC, useCallback, useEffect, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";

import { existsAndNotEmpty } from "stentor-utils";
import { Button, ButtonProps, Dialog, LoadingSpinner, Onboarding, OnboardingStep, PageAlert } from "@xapp/components";

import { TEMPLATES, Template } from "constants/templates";
import { useGetUserProfileQuery, useOnboardingAppQuery } from "generated/graphql";
import { getDefaultProduct, getProducts, supportsIntegrations } from "utils/getProducts";
import { isDev } from "utils/environment";

import { OnboardingProps } from "../models";

import { AddressStepContainer } from "./AddressStepContainer";
import { GoogleBusinessProfileQuestionStepContainer } from "./GoogleBusinessProfileQuestionStepContainer";
import { SimpleConfigureAndCreateStepContainer } from "./SimpleConfigureAndCreateStepContainer";
import { CheckoutStepContainer } from "./CheckoutStepContainer";
import { IntegrateStepContainer } from "./IntegrateStepContainer";
import { WebsiteQuestionStepContainer } from "./WebsiteQuestionStepContainer";
import { FinalizeContainer } from "./FinalizeContainer";

import style from "./style.scss";
import { SocialQuestionStepContainer } from "./SocialQuestionStepContainer";
import { SendLeadsStepContainer } from "./SendLeadsStepContainer";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

function useAddQueryParams() {
    const history = useHistory();

    type QueryParamsType = { [key: string]: string };
    return useCallback(
        (newParams: QueryParamsType) => {
            const searchParams = new URLSearchParams(history.location.search);
            for (const key in newParams) {
                const value = newParams[key];
                if (value && value !== "null") {
                    searchParams.set(key, newParams[key]);
                }
            }
            history.replace({ ...history.location, search: searchParams.toString() });
        },
        [history]
    );
}

export const LEADS_PRODUCT_ID = "prod_QWRVWvZtQH6dBI";

export const OnboardingContainer: FC = () => {
    const history = useHistory();

    const setQueryParams = useAddQueryParams();
    const queryParams = useQueryParams();

    const email = queryParams.get("email");
    const website = queryParams.get("website");

    const appId = queryParams.get("appId");
    const organizationId = queryParams.get("organizationId");
    const executionId = queryParams.get("executionId");

    const cms = queryParams.get("cms");
    const partner = queryParams.get("partner");
    let product = queryParams.get("product");

    if (product === "starter" || !product) {
        product = getDefaultProduct();
    }

    const queryStep = queryParams.get("step");
    const coupon = queryParams.get("coupon");
    const freeTrial = queryParams.get("freeTrial");

    const beta = queryParams.get("beta");
    const legacy = queryParams.get("legacy");
    const debug = queryParams.get("debug");
    const demo = queryParams.get("demo");

    const productDetails = getProducts().find((p) => p.id === product);
    const productName = productDetails?.name;
    const isLeads = product === LEADS_PRODUCT_ID;

    const [showWarning, setShowWarning] = useState<boolean>(false);
    const [answeredWarning, setAnsweredWarning] = useState<boolean>(false);

    const [redirectUrl, setRedirectUrl] = useState<string>();

    // this doesn't work if they are going to onboarding/sign-up
    const [getProfile] = useGetUserProfileQuery({
        requestPolicy: "cache-and-network",
        variables: {},
    });

    const [getOnboardingApp] = useOnboardingAppQuery({
        pause: !appId,
        variables: {
            appId,
        },
    });

    // These keep track of the props that will create the app as we move steps
    const [onBoardingProps, setOnboardingProps] = useState<OnboardingProps>(() => {
        // in case the user refreshes, we store them (down below) on session storage
        // and try to retreive them
        let storageProps: OnboardingProps;
        try {
            storageProps = JSON.parse(sessionStorage.getItem("onboarding-appProps"));
        } catch (e) {
            // don't care about the error
            console.error(`Error parsing session storage onboarding-appProps: ${e}`);
        }
        return storageProps || {};
    });

    // DEBUG
    const clearAppProps = useCallback(() => {
        setOnboardingProps({ currentStep: "" });
        sessionStorage.removeItem("onboarding-appProps");
    }, []);

    /** Transition to step "website"  */
    const handleInitialAppCreated = useCallback(
        async (createProps: OnboardingProps) => {
            setOnboardingProps((prevAppProps) => {
                const newAppProps: OnboardingProps = {
                    ...prevAppProps,
                    ...createProps,
                    currentStep: "website",
                };

                // save them to local storage too
                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newAppProps));

                return newAppProps;
            });

            setQueryParams({
                appId: createProps.appId,
                organizationId: createProps.organizationId,
                website: createProps.website,
                step: "website",
                product,
                coupon,
                partner,
                email,
                cms,
                beta,
                legacy,
                executionId: createProps.executionId,
                debug,
                demo,
            });
        },
        [beta, cms, coupon, debug, demo, email, legacy, partner, product, setQueryParams]
    );

    // End of Step "website"
    const handleWebsiteInput = useCallback(
        async (createProps: OnboardingProps) => {
            setOnboardingProps((prevAppProps) => {
                const newAppProps: OnboardingProps = {
                    ...prevAppProps,
                    ...createProps,
                    currentStep: "profile",
                };

                // save them to local storage too
                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newAppProps));

                return newAppProps;
            });

            setQueryParams({
                appId: createProps.appId,
                organizationId: createProps.organizationId,
                website: createProps.website,
                step: "profile",
                product,
                coupon,
                partner,
                email,
                cms,
                beta,
                legacy,
                executionId: createProps.executionId,
                debug,
                demo,
            });
        },
        [beta, cms, coupon, debug, demo, email, legacy, partner, product, setQueryParams]
    );

    // End of Step "profile"
    const handleProfileQuestion = useCallback(
        async (createProps: OnboardingProps) => {
            setOnboardingProps((prevAppProps) => {
                const newAppProps: OnboardingProps = {
                    ...prevAppProps,
                    ...createProps,
                    currentStep: "address",
                };

                // save them to local storage too
                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newAppProps));

                return newAppProps;
            });
            setQueryParams({
                appId: createProps.appId,
                organizationId: createProps.organizationId,
                website: createProps.website,
                step: "address",
                product,
                coupon,
                partner,
                email,
                cms,
                beta,
                legacy,
                executionId: createProps.executionId,
                debug,
                demo,
            });
        },
        [beta, cms, coupon, debug, demo, email, legacy, partner, product, setQueryParams]
    );

    /** Transition to "address" */
    const handleAddressInput = useCallback(
        async (createProps: OnboardingProps) => {
            setOnboardingProps((prevAppProps) => {
                const newAppProps: OnboardingProps = {
                    ...prevAppProps,
                    ...createProps,
                    currentStep: "social",
                };

                // TODO: is it possible to get a website from place if they didn't provide us with one?
                // do we then start an analysis

                // save them to local storage too
                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newAppProps));

                return newAppProps;
            });

            // Add the place
            setQueryParams({
                appId,
                organizationId,
                website,
                step: "social",
                product,
                coupon,
                partner,
                email,
                cms,
                beta,
                legacy,
                executionId,
                debug,
                demo,
            });
        },
        [
            appId,
            beta,
            cms,
            coupon,
            debug,
            demo,
            email,
            executionId,
            legacy,
            organizationId,
            partner,
            product,
            setQueryParams,
            website,
        ]
    );

    const handleSocialQuestion = useCallback(
        async (createProps: OnboardingProps) => {
            setOnboardingProps((prevAppProps) => {
                const newAppProps: OnboardingProps = {
                    ...prevAppProps,
                    ...createProps,
                    currentStep: "integrate",
                };

                // TODO: is it possible to get a website from place if they didn't provide us with one?
                // do we then start an analysis

                // save them to local storage too
                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newAppProps));

                return newAppProps;
            });

            // Add the place
            setQueryParams({
                appId,
                organizationId,
                website,
                step: "integrate",
                product,
                coupon,
                partner,
                email,
                cms,
                beta,
                legacy,
                executionId,
                debug,
                demo,
            });
        },
        [
            appId,
            beta,
            cms,
            coupon,
            debug,
            demo,
            email,
            executionId,
            legacy,
            organizationId,
            partner,
            product,
            setQueryParams,
            website,
        ]
    );

    const handleUpgrade = useCallback(() => {
        const searchParams = new URLSearchParams(history.location.search);

        const products = getProducts();

        const leadsProduct = products.find((product) => {
            return product.name === "XAPP AI Leads+";
        });

        // this is when they request to upgrade to leads and we need track it for payment
        if (leadsProduct) {
            searchParams.set("product", leadsProduct.id);
        }

        if (history.location.search !== searchParams.toString()) {
            history.replace({ ...history.location, search: searchParams.toString() });
        }
    }, [history]);

    /** Transition to step "checkout" */
    const handleIntegrateComplete = useCallback(
        (createProps: OnboardingProps) => {
            // update app
            setOnboardingProps((prevAppProps) => {
                const newAppProps: OnboardingProps = {
                    ...prevAppProps,
                    ...createProps,
                    currentStep: "checkout",
                };

                // TODO: is it possible to get a website from place if they didn't provide us with one?
                // do we then start an analysis

                // save them to local storage too
                sessionStorage.setItem("onboarding-appProps", JSON.stringify(newAppProps));

                return newAppProps;
            });

            setQueryParams({
                appId,
                organizationId,
                website,
                step: "checkout",
                product,
                coupon,
                partner,
                email,
                cms,
                beta,
                legacy,
                executionId,
                debug,
                demo,
            });
        },
        [
            setQueryParams,
            appId,
            organizationId,
            website,
            product,
            coupon,
            partner,
            email,
            cms,
            beta,
            legacy,
            executionId,
            debug,
            demo,
        ]
    );

    const handleBack = useCallback(
        (step: string) => {
            let newStep = step;

            switch (step) {
                case "profile":
                    newStep = "website";
                    break;
                case "address":
                    newStep = "profile";
                    break;
                case "social":
                    newStep = "address";
                    break;
                case "integrate":
                    newStep = "social";
                    break;
                default:
                    throw new Error(`Unhandled step ${step}`);
            }

            setQueryParams({
                appId,
                organizationId,
                website,
                step: newStep,
                product,
                coupon,
                partner,
                email,
                cms,
                beta,
                legacy,
                executionId,
                debug,
                demo,
            });
        },
        [
            appId,
            beta,
            cms,
            coupon,
            debug,
            demo,
            email,
            executionId,
            legacy,
            organizationId,
            partner,
            product,
            setQueryParams,
            website,
        ]
    );

    const handleRedirectToHome = useCallback(() => {
        // Clear out the onboarding props from session storage?
        sessionStorage.removeItem("onboarding-appProps");

        // Redirect them to app home
        let successPath = `/${organizationId}/${appId}/getting-started?checkout=success`;
        if (cms) {
            successPath += `&cms=${cms}`;
        }

        if (partner) {
            successPath += `&partner=${partner}`;
        }

        // go to the successPath
        setRedirectUrl(successPath);
    }, [appId, cms, organizationId, partner]);

    let welcomeMessage: string | undefined;

    switch (partner) {
        case "servicetitan":
            welcomeMessage = "Welcome ServiceTitan Customers!";
            break;
        case "fieldpulse":
            welcomeMessage = "Welcome FieldPulse Customers!";
            break;
        case "lacrm":
            welcomeMessage = "Welcome Less Annoying CRM Customers!";
            break;
        case "jobber":
            welcomeMessage = "Welcome Jobber Customers!";
            break;
        case "sfl":
            welcomeMessage = "Welcome Surefire Local Customers!";
            break;
        case "housecallpro":
            welcomeMessage = "Welcome Housecall Pro Customers!";
            break;
        case "angi":
            welcomeMessage = "Welcome Angi Customers!";
            break;
        default:
            welcomeMessage = undefined;
    }

    // copy the params
    const successPathParams = new URLSearchParams(history.location.search);
    successPathParams.set("step", "finalize");

    const successPath = `/onboarding?${successPathParams.toString()}`;

    const { data, error, fetching } = getProfile;
    const { data: onboardingAppData, error: onboardingAppError, fetching: onboardingAppFetching } = getOnboardingApp;

    const app = onboardingAppData?.app;
    const profile = data?.profile;

    const roles = profile?.roles || [];

    if (roles.length === 1) {
        // TODO: get the app on the org
    }

    if (roles.length >= 1 && demo?.toLowerCase() !== "true" && !answeredWarning && !appId) {
        // let them know they have multiple
        if (!showWarning) {
            setShowWarning(true);
        }
    }

    // Aligns app with query string and onboarding props
    useEffect(() => {
        if (app) {
            let step = queryStep;

            if (!step) {
                // determine step they should be on.

                // do they have a website or answered the website question
                const answeredWebsite = app.website || app.beta?.onboarding?.hasWebsite;
                const answeredPlace = app.places && app.places.length > 0;
                const answeredIntegrate = app.beta?.onboarding?.leadsIntegration;

                if (!answeredWebsite) {
                    step = "website";
                } else if (!answeredPlace) {
                    step = "address";
                } else if (!answeredIntegrate) {
                    step = "integrate";
                }
            }

            const template = TEMPLATES.find((template: Template) => template.id === app.templateType);
            const chatWidgetId = app.channels.find((channel) => channel.type === "chat-widget")?.id;
            const searchWidgetId = app.channels.find((channel) => channel.type === "intelligent-search")?.id;
            const formWidgetId = app.channels.find((channel) => channel.type === "form-widget")?.id;
            const lexChannelId = app.channels.find((channel) => channel.type === "lex-v2")?.id;

            setOnboardingProps({
                appId: app.appId,
                organizationId: app.organizationId,
                name: app.name,
                website: app.website,
                template,
                place: app?.places && app.places.length > 0 ? app.places[0] : undefined,
                leadsContact: app.leadsContact,
                beta: app.beta,
                currentStep: step,
                chatWidgetId,
                searchWidgetId,
                formWidgetId,
                lexChannelId,
                executionId: app.websiteData?.crawlExecutionId,
                primaryColor: app.websiteData?.primaryColor,
                secondaryColor: app.websiteData?.secondaryColor,
            });

            setQueryParams({
                appId: app.appId,
                organizationId: app.organizationId,
                website: app.website,
                step,
                product,
                coupon,
                partner,
                email,
                cms,
                beta,
                legacy,
                executionId,
                debug,
                demo,
            });
        }
    }, [app, beta, cms, coupon, queryStep, debug, demo, email, executionId, legacy, partner, product, setQueryParams]);

    if (redirectUrl) {
        return <Redirect to={redirectUrl} />;
    }

    if (fetching || onboardingAppFetching) {
        return <LoadingSpinner loadingText={"Getting your profile..."} />;
    }

    if (error || onboardingAppError) {
        return (
            <PageAlert background={"orange"}>
                <p>
                    We had an error retrieving your profile. {error?.message || onboardingAppError} You can try logging
                    in again <a href="/login">here.</a>
                </p>
            </PageAlert>
        );
    }

    const warningActions: ButtonProps[] = [
        {
            label: "Continue",
            variation: "secondary",
            onClick: () => {
                setAnsweredWarning(true);
                setShowWarning(false);
                clearAppProps();
            },
        },
        {
            label: "Show me Existing",
            onClick: () => {
                setRedirectUrl(`/list`);
            },
        },
    ];

    return (
        <>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                <img
                    src={"/img/xapp-ai-horizontal-2color-rgb.png"}
                    alt="logo"
                    style={{ maxHeight: "50px", width: "auto", display: "block" }}
                />
            </div>
            <Onboarding>
                <OnboardingStep
                    title={"Welcome"}
                    active={!queryStep || queryStep === "welcome"}
                    done={
                        queryStep === "website" ||
                        queryStep === "profile" ||
                        queryStep === "address" ||
                        queryStep === "integrate" ||
                        queryStep === "checkout" ||
                        queryStep === "terms" ||
                        queryStep === "review" ||
                        queryStep === "social"
                    }
                >
                    <SimpleConfigureAndCreateStepContainer
                        debug={isDev()}
                        profile={profile}
                        welcomeMessage={welcomeMessage}
                        initialProps={onBoardingProps}
                        product={product}
                        className={style.stepWrapper}
                        onNextStep={handleInitialAppCreated}
                        beta={beta === "true"}
                    />
                </OnboardingStep>
                <OnboardingStep
                    title={"Website"}
                    hide={true}
                    active={queryStep === "website"}
                    done={
                        queryStep === "profile" ||
                        queryStep === "address" ||
                        queryStep === "integrate" ||
                        queryStep === "checkout" ||
                        queryStep === "terms" ||
                        queryStep === "review" ||
                        queryStep === "social"
                    }
                >
                    <WebsiteQuestionStepContainer
                        initialProps={onBoardingProps}
                        className={style.stepWrapper}
                        profile={profile}
                        product={product}
                        onNextStep={handleWebsiteInput}
                    />
                </OnboardingStep>

                <OnboardingStep
                    title={"Profile"}
                    hide={true}
                    active={queryStep === "profile"}
                    done={
                        queryStep === "address" ||
                        queryStep === "integrate" ||
                        queryStep === "checkout" ||
                        queryStep === "terms" ||
                        queryStep === "review" ||
                        queryStep === "social"
                    }
                >
                    <GoogleBusinessProfileQuestionStepContainer
                        initialProps={onBoardingProps}
                        className={style.stepWrapper}
                        profile={profile}
                        product={product}
                        onBack={handleBack.bind(null, "profile")}
                        onNextStep={handleProfileQuestion}
                    />
                </OnboardingStep>

                <OnboardingStep
                    title="Address"
                    active={queryStep === "address"}
                    hide={true}
                    done={
                        queryStep === "integrate" ||
                        queryStep === "checkout" ||
                        queryStep === "terms" ||
                        queryStep === "review" ||
                        queryStep === "social"
                    }
                >
                    <AddressStepContainer
                        app={onBoardingProps}
                        className={style.stepWrapper}
                        onBack={handleBack.bind(null, "address")}
                        onNext={handleAddressInput}
                    />
                </OnboardingStep>

                <OnboardingStep
                    title="Social"
                    active={queryStep === "social"}
                    hide={true}
                    done={
                        queryStep === "integrate" ||
                        queryStep === "checkout" ||
                        queryStep === "terms" ||
                        queryStep === "review" ||
                        queryStep === "address"
                    }
                >
                    <SocialQuestionStepContainer
                        initialProps={onBoardingProps}
                        className={style.stepWrapper}
                        profile={profile}
                        product={product}
                        onBack={handleBack.bind(null, "social")}
                        onNextStep={handleSocialQuestion}
                    />
                </OnboardingStep>

                {/* Only show on leads, not leads+ or scheduler, otherwise show existing route */}
                {isLeads ? (
                    <OnboardingStep
                        title={"Leads Email"}
                        active={queryStep === "integrate"}
                        done={queryStep === "checkout" || queryStep === "terms" || queryStep === "review"}
                    >
                        <SendLeadsStepContainer
                            app={onBoardingProps}
                            /** Based on if they are starter or not */
                            integrationsSupported={supportsIntegrations(product)}
                            limitIntegrations={partner ? [partner] : undefined}
                            className={style.stepWrapper}
                            onUpgrade={handleUpgrade}
                            onBack={handleBack.bind(null, "integrate")}
                            onNext={handleIntegrateComplete}
                        />
                    </OnboardingStep>
                ) : (
                    <OnboardingStep
                        title={"Integrate"}
                        active={queryStep === "integrate"}
                        done={queryStep === "checkout" || queryStep === "terms" || queryStep === "review"}
                    >
                        <IntegrateStepContainer
                            app={onBoardingProps}
                            integrationsSupported={supportsIntegrations(product)}
                            limitIntegrations={partner ? [partner] : undefined}
                            className={style.stepWrapper}
                            onUpgrade={handleUpgrade}
                            onBack={handleBack.bind(null, "integrate")}
                            onNext={handleIntegrateComplete}
                        />
                    </OnboardingStep>
                )}

                <OnboardingStep
                    title={freeTrial?.toLowerCase() === "false" ? "Payment" : "Start Trial"}
                    active={queryStep === "start-trial" || queryStep === "checkout" || queryStep === "terms"}
                    done={queryStep === "review"}
                >
                    <CheckoutStepContainer
                        successPath={successPath}
                        couponId={coupon}
                        productId={product}
                        noTrial={freeTrial?.toLowerCase() === "false"}
                        integrationsSupported={supportsIntegrations(product)}
                        cms={cms}
                        partner={partner}
                        app={onBoardingProps}
                        className={style.stepWrapper}
                    />
                </OnboardingStep>
                <OnboardingStep title={"Finalizing"} active={queryStep === "finalize"} hide={true}>
                    <FinalizeContainer
                        profile={profile}
                        onboardingProps={onBoardingProps}
                        className={style.stepWrapper}
                        onNextStep={handleRedirectToHome}
                    />
                </OnboardingStep>
                {/* Removing
                <OnboardingStep title={"Create"} active={queryStep === "configure" || queryStep === "review"}>
                    <ConfigureAndCreateStepContainer
                        initialProps={onBoardingProps}
                        executionId={executionId}
                        productId={product}
                        className={style.stepWrapper}
                        onNextStep={handleRedirectToHome}
                        beta={beta === "true"}
                    />
                </OnboardingStep> */}
            </Onboarding>
            <Dialog active={showWarning} title={"Are you sure you want to start over?"} actions={warningActions}>
                <p>You have already created an AI assistant for a business.</p>
            </Dialog>
            {debug === "true" && (
                <div
                    style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignContent: "center",
                        alignItems: "center",
                    }}
                >
                    <div
                        style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flexWrap: "wrap" }}
                    >
                        <b>Debug</b>
                        <p>Business: {onBoardingProps?.name}</p>
                        <p>App: {onBoardingProps?.appId}</p>
                        <p>
                            Template: {onBoardingProps?.template?.name} ({onBoardingProps?.template?.id})
                        </p>
                        <p>Website: {onBoardingProps?.website}</p>
                        <p>Execution Id: {onBoardingProps?.executionId}</p>
                        <p>
                            Place: {onBoardingProps?.place?.formattedAddress} ({onBoardingProps?.place?.placeId})
                        </p>
                        <p>
                            Integration:{" "}
                            {existsAndNotEmpty(onBoardingProps?.beta?.onboarding?.leadsIntegration)
                                ? onBoardingProps?.beta?.onboarding?.leadsIntegration[0]
                                : ""}
                        </p>
                        <p>Leads Email: {onBoardingProps?.leadsContact}</p>
                        <p>
                            Channels: {onBoardingProps.lexChannelId} {onBoardingProps.chatWidgetId}-
                            {onBoardingProps.formWidgetId}-{onBoardingProps.searchWidgetId}
                        </p>
                        <p>
                            Product: {productName} {product}
                        </p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "row", margin: "20px" }}>
                        <Button variation="secondary" label="Clear Props" onClick={clearAppProps} />
                        <Button label="Print Props" onClick={() => console.log(onBoardingProps)} />
                    </div>
                </div>
            )}
        </>
    );
};
